<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between">
      <div class="d-flex">
        <h6 class="d-flex">
          <v-btn fab small elevation="0" color="std_gray" class="mr-2">
            <v-icon>{{ icon }}</v-icon>
          </v-btn>
          <span class="d-flex flex-column">
          <span>{{ title }}</span>
          <span class="text-caption text-truncate">{{ subtitle }}</span>
        </span>
        </h6>
      </div>
      <div class="filter-chips align-center">
        <toggle-chip v-model="violationsFlag" :badge-value="numRoutesWithViolations" class="mr-3">
          {{ $tc('violation', 2) }}
        </toggle-chip>
        <toggle-chip v-model="withoutViolationsFlag" :badge-value="numRoutesWithoutViolations" class="mr-2">
          {{ $t('no_violations') }}
        </toggle-chip>
      </div>
      <div class="align-self-center">
        <info-modal>
          <v-card-title>
            <h3>{{ title }}</h3>
          </v-card-title>
          <v-card-text>
            <div v-html="infoText"></div>
          </v-card-text>
        </info-modal>
      </div>
    </v-card-title>
    <v-card-text class="mt-n2 pb-2">
      <div v-if="hasForecastingEnabled" class="d-flex justify-end align-center mb-1">
        <span class="mr-2">{{ $t('forecast_violation_legend') }}</span>
        <span class="mr-1">
          <icon-tooltip color="temperature-cold" icon="mdi-weather-cloudy-alert" :tooltip="$t('forecast_cold_violation')"/>
        </span>
        <icon-tooltip color="temperature-hot" icon="mdi-weather-cloudy-alert" :tooltip="$t('forecast_hot_violation')"/>
      </div>
      <v-data-table
        class="vf-table mt-2"
        :headers="tableFields"
        :items="sortedRoutes"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :loading="loading"
        :loading-text="$t('loading')">
        <!-- Only a stateless button lives in the header: Vuetify renders the
             header `<th>`s without keys, so this node is destroyed and
             re-created whenever a column is hidden or shown. The configurator
             itself is mounted below, outside the table, so the open dialog
             survives a toggle. -->
        <template v-slot:header.__columnConfigurator>
          <v-btn
            icon
            small
            class="column-configurator-activator"
            :aria-label="$t('configure_columns')"
            @click.stop="openColumnConfigurator">
            <v-icon small>mdi-cog</v-icon>
          </v-btn>
        </template>
        <template v-slot:item.route="row">
          <router-link :to="`/route/${row.item.id}`" v-if="!user.can('view_display')">
            {{ row.item.name }}
          </router-link>
          <div v-else>
            {{ row.item.name }}
          </div>
        </template>
        <template v-slot:item.vehicle="row">
          <router-link :to="`/vehicle/${row.item.vehicle.id}`">
            {{ row.item.vehicle.name }}
          </router-link>
        </template>
        <template v-for='(column, index) in routeProductColumns' v-slot:[`item.${column.value}`]="row">
          <route-product-status
            :route="row.item"
            :route-product="get(row.item.product_indexed_route_products, column.product_id)"
            :forecast-violation="routeProductForecastViolation(row.item, get(row.item.product_indexed_route_products, column.product_id))"
            :key="index"
          />
        </template>
      </v-data-table>
      <!-- Mounted outside the table on purpose (see the header slot above):
           this node's position never changes, so the dialog stays open while
           the user toggles and reorders columns. Renders nothing while the
           feature flag is off. -->
      <column-configurator
        ref="columnConfigurator"
        hide-activator
        :table-id="tableId"
        :columns="resolvedColumns"
        :default-columns="defaultColumnRegistry"
        @update:columns="onColumnsUpdate"
        @reset="onColumnsReset" />
      <div>
        <p class="text-right mb-0">
          <span v-if="hasRouteCreationEnabled">{{ $t('automatic_route_creation_enabled') }}</span>
          <span v-else>{{ $t('automatic_route_creation_disabled') }}</span>
        </p>
      </div>
    </v-card-text>
  </v-card>

</template>

<script>

import { each, get, some, filter, sortBy, reverse, cloneDeep } from 'lodash'
import ToggleChip from '@/components/shared/ToggleChip'
import RouteProductStatus from '@/components/route/RouteProductStatus'
import IconTooltip from '@/components/shared/IconTooltip'
import Moment from 'moment'
import InfoModal from '@/components/shared/InfoModal'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ColumnConfigurator, { COLUMN_CONFIGURATOR_HEADER } from '@/components/shared/ColumnConfigurator'
import TableColumnPreferencesMixin from '@/components/mixins/TableColumnPreferencesMixin'

// Common fields for requests
const expand = [
  'vehicle',
  'route_products',
  'route_products.product',
  'route_products.violations',
  'forecast_route_violations.route_product'
]
const fields = [
  'id',
  'name',
  'start',
  'end',
  'vehicle.name',
  'vehicle.id',
  'route_products.*',
  'route_products.product.*',
  'route_products.violations.type',
  'route_products.violations.level',
  'route_products.violations.message',
  'route_products.violations.start',
  'route_products.violations.end',
  'forecast_route_violations.*'
]

/**
 * Persistence contract for this card (epic VF-4136). The Route Dashboard
 * renders this component twice, so each instance owns a SEPARATE entry keyed
 * by its `type` prop: the two tables list different routes and therefore carry
 * different product columns, and a shared id would let each instance's
 * registry prune the other's saved product ids on every load. Both values are
 * FIXED — renaming an id orphans every preference already stored, and the
 * integer version is bumped only for a deliberate registry migration.
 */
export const ROUTE_DASHBOARD_CARD_TABLE_IDS = {
  active: 'RouteDashboardCard.active',
  recent: 'RouteDashboardCard.recent'
}
export const ROUTE_DASHBOARD_CARD_COLUMNS_VERSION = 1

export default {
  name: 'RouteDashboardCard',
  mixins: [
    CurrentUserMixin,
    TableColumnPreferencesMixin
  ],
  components: {
    ToggleChip,
    RouteProductStatus,
    IconTooltip,
    InfoModal,
    ColumnConfigurator
  },
  props: {
    type: {
      type: String,
      required: true
    },
    title: {
      type: String
    },
    subtitle: {
      type: String
    },
    icon: {
      type: String
    },
    infoText: {
      type: String
    },
    activeRefresh: {
      type: Boolean,
      default: false
    },
    customer: Object
  },
  data () {
    return {
      loading: false,
      loaded: false,
      violationsFlag: true,
      withoutViolationsFlag: true,
      sortBy: null,
      sortDesc: false,
      routes: [],
      routeProductColumns: [],
      /**
       * True once the first fetch has built routeProductColumns, i.e. once the
       * column registry below is complete. See tableColumnDefaults.
       */
      columnRegistryReady: false,
      routeDetection: {},
      refreshTimeoutId: null
    }
  },
  computed: {
    hasForecastingEnabled () {
      return this.customerContext.getSetting('enableSimulatedTemperatureForecast') === 1
    },
    hasRouteCreationEnabled () {
      return get(this.customerContext.getSetting('processing.RouteDetection'), 'enableRouteStarting')
    },
    /**
     * Sort the filtered routes (the final step)
     * @returns Array
     */
    sortedRoutes () {
      let result
      switch (this.sortBy) {
        case 'violation':
          result = sortBy(this.filteredRoutes, x => x.has_fatal_violation)
          break
        case 'route':
          result = sortBy(this.filteredRoutes, x => x.name)
          break
        case 'vehicle':
          result = sortBy(this.filteredRoutes, x => x.vehicle.name)
          break
        default:
          result = this.filteredRoutes
          break
      }
      if (this.sortDesc === true) {
        // array reverse mutates the array, so clone first.
        return reverse(cloneDeep(result))
      } else {
        return result
      }
    },
    /**
     * The number of routes with violations (used for the v-chip display)
     * @returns {number}
     */
    numRoutesWithViolations () {
      return filter(this.routes, x => x.has_fatal_violation === true).length
    },
    /**
     * The number of routes without violations (used for the v-chip display)
     * @returns {number}
     */
    numRoutesWithoutViolations () {
      return filter(this.routes, x => x.has_fatal_violation === false).length
    },
    /**
     * Filter the retrieved routes by which chips are selected. This is the intermediate step - we then sort.
     * @returns Array
     */
    filteredRoutes () {
      if (this.violationsFlag && this.withoutViolationsFlag) {
        return this.routes
      } else if (this.violationsFlag) {
        return filter(this.routes, x => x.has_fatal_violation === true)
      } else if (this.withoutViolationsFlag) {
        return filter(this.routes, x => x.has_fatal_violation === false)
      } else {
        return []
      }
    },
    /**
     * The id this instance's saved preferences are keyed by, derived from the
     * `type` prop so the Active card and the Recent card never share an entry.
     * An unrecognised type still gets its own namespaced id rather than
     * silently colliding with one of the two real ones.
     *
     * @returns String
     */
    tableId () {
      return ROUTE_DASHBOARD_CARD_TABLE_IDS[this.type] || `RouteDashboardCard.${this.type}`
    },
    /**
     * This card's built-in column registry in default display order: Route
     * pinned first, then Vehicle, then one column per product carried by the
     * routes currently listed. `id` always equals the Vuetify header `value`;
     * product ids are product_{product_id} (VF-4177) so a preference follows
     * the product. The product entries keep the header attributes this table
     * has always rendered them with, plus product_id for the status slot — the
     * configurator preserves unknown fields untouched.
     *
     * Air temperature columns are deliberately absent: the dashboard does not
     * load that data yet (VF-4392).
     *
     * @returns Array
     */
    defaultColumnRegistry () {
      const columns = [
        { id: 'route', label: this.$tc('route'), visible: true, primary: true, hideable: false },
        { id: 'vehicle', label: this.$tc('vehicle'), visible: true, primary: false, hideable: true }
      ]
      each(this.routeProductColumns, column => {
        columns.push({
          id: column.value,
          label: column.text,
          visible: true,
          primary: false,
          hideable: true,
          product_id: column.product_id,
          sortable: column.sortable,
          align: column.align,
          class: column.class
        })
      })
      return columns
    },
    /**
     * Override of TableColumnPreferencesMixin's computed: the single table this
     * instance owns.
     *
     * Published only once the first fetch has built the product columns. The
     * mixin's mounted() convergence runs BEFORE fetchRoutes(), when the
     * registry is still Route + Vehicle; it would read every saved product id
     * as removed, prune them, persist the pruned entry and hold a pending
     * overlay that keeps the product columns off the table for the rest of the
     * session. An empty object makes that convergence a no-op for this card.
     *
     * @returns Object
     */
    tableColumnDefaults () {
      if (!this.columnRegistryReady) {
        return {}
      }
      return {
        [this.tableId]: {
          version: ROUTE_DASHBOARD_CARD_COLUMNS_VERSION,
          columns: this.defaultColumnRegistry
        }
      }
    },
    /**
     * The registry actually in effect: saved order and visibility merged into
     * the defaults, the raw defaults while the feature flag is off, and the raw
     * defaults during the first fetch (when this card publishes no defaults).
     *
     * @returns Array
     */
    resolvedColumns () {
      return this.resolvedTableColumns[this.tableId] || this.defaultColumnRegistry
    },
    /**
     * Get the table columns: the visible registry columns in the effective
     * order, carrying their header attributes, plus the synthetic gear header
     * appended last once the feature is on and the registry is complete
     * (VF-4197).
     *
     * @returns Array
     */
    tableFields () {
      const fields = []
      each(this.resolvedColumns, column => {
        if (!column.visible) {
          return
        }
        const field = { text: column.label, value: column.id }
        each(['product_id', 'sortable', 'align', 'class'], key => {
          if (column[key] !== undefined) {
            field[key] = column[key]
          }
        })
        fields.push(field)
      })
      if (this.configurableTableColumnsEnabled && this.columnRegistryReady) {
        fields.push(COLUMN_CONFIGURATOR_HEADER)
      }
      return fields
    }
  },
  methods: {
    get: get,
    /**
     * Open the configurator modal from the gear button rendered in the table
     * header. The modal itself lives outside the table (see the template) so it
     * is not re-created when the header row changes length.
     */
    openColumnConfigurator () {
      this.$refs.columnConfigurator.openDialog()
    },
    /**
     * Return true if any route product has a fatal violation.
     * @param route
     * @returns Boolean
     */
    hasAFatalViolation (route) {
      return some(route.route_products, rp => {
        return some(rp.violations, v => v.level === 'fatal')
      })
    },
    /**
     * Load the routes from the API.
     * @return void
     */
    async fetchRoutes () {
      this.loading = true
      this.loaded = false
      try {
        const params = {
          expand: expand.join(','),
          fields: fields.join(','),
          sort: 'start',
          'per-page': 200
        }
        // Alter the API query based on what we're showing in this card
        if (this.type === 'recent') {
          // "Recent" means recently *ended*: end within the last 24 hours, but not in the
          // future. Without the end_before cap, an ongoing route with a future end date would
          // satisfy end_after (end > now-24h) and appear here as well as under Active (VF-4131).
          params.end_after = Moment().subtract(24, 'hours').format()
          params.end_before = Moment().format()
        } else if (this.type === 'active') {
          params.active = 1
        }
        const response = await this.$orm('Route').findAll(params)
        this.routes = response.getItems()
        // Add an additional property to help us out a little with filtering violations
        each(this.routes, r => {
          r.has_fatal_violation = this.hasAFatalViolation(r)
        })
        this.loaded = true
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
        this.buildRouteProductColumns()
        // The product columns are part of the column registry, so saved
        // preferences can only be resolved once they exist (see
        // tableColumnDefaults).
        this.columnRegistryReady = true
        // If actively refreshing, queue another call to refresh the dashboard in 1 minute. We re-queue the task
        // this way rather than using setInterval() in order to ensure that we don't overlap calls (in case the API is
        // slow to respond or we're on a very slow network)
        if (this.refreshTimeoutId !== null) {
          clearTimeout(this.refreshTimeoutId)
          this.refreshTimeoutId = null
        }
        if (this.activeRefresh === true) {
          this.refreshTimeoutId = setTimeout(this.fetchRoutes, 60 * 1000)
        }
      }
    },
    /**
     * Build a list of route product columns to display in the table. This will get added to the base list of columns.
     */
    buildRouteProductColumns () {
      const result = []
      // Build a map of product ids to product names by iterating through each route's route products.
      const productsMap = {}
      each(this.routes, route => {
        each(route.route_products, routeProduct => {
          const productName = get(routeProduct, 'product.name', null);
          productsMap[routeProduct.product_id] = productName !== null ? productName : this.$t('unknown');
        });
      });
      // Push each product entry into the fields
      each(productsMap, (name, id) => {
        result.push({
          text: name,
          product_id: id,
          value: `product_${id}`,
          sortable: false,
          align: 'end',
          class: 'wrap'
        })
      })
      this.routeProductColumns = result
    },
    /**
     * Returns the forecasted violation associated with the route product specified or null if one is not found
     *
     * @param {RouteRecord} route
     * @param {RouteProductRecord} routeProduct
     * @return Object|null
     */
    routeProductForecastViolation (route, routeProduct) {
      if (!routeProduct) {
        return null
      }
      const forecastedRouteViolations = get(route, 'forecast_route_violations', [])
      const forecastedRouteProductViolations = filter(
        forecastedRouteViolations, (forecastViolation) => {
          return forecastViolation.route_product.id === routeProduct.id
        }
      )
      return forecastedRouteProductViolations.length === 1 ? forecastedRouteProductViolations[0] : null
    },

    /**
     * Refresh customer list when customer context is changed (fired by the CurrentUserMixin)
     */
    customerContextChangeHandler () {
      this.fetchRoutes()
    }
  },
  mounted () {
    this.fetchRoutes()
  },
  beforeDestroy () {
    if (this.refreshTimeoutId !== null) {
      clearTimeout(this.refreshTimeoutId)
      this.refreshTimeoutId = null
    }
  }
}
</script>

<style scoped lang="scss">
</style>
